import { useEffect, useRef, useState } from 'react'
import { useChat } from '../contexts/ChatContext'

/**
 * THE ROSTER, EDITED WHERE IT IS READ. One chip per member wearing the
 * header stack's portrait; the shell's own chip grammar (prop-chip, the ×,
 * the select menu) for the editing, so the row reads like every other
 * editable value in the ledger and invents no vocabulary of its own.
 *
 * Who can be added is the parent GROUP's roster minus the channel's: the
 * same set the backend admits, so nothing offered here is ever refused.
 * A DM's roster is fixed, so a DM shows its chips and no editing.
 * Removing yourself is "leave", which has its own door; the × stays off
 * your own chip.
 */
export default function ChannelMembersField({ channel, members = [], avatarColor }) {
  const { currentUser, groupMembers, addChannelMember, removeChannelMember } = useChat()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(null)
  const ref = useRef(null)

  const editable = !!channel?.group_id

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const inChannel = new Set(members.map((m) => m.user_id))
  const q = query.trim().toLowerCase()
  const seen = new Set()
  const candidates = (groupMembers || [])
    .map((m) => ({
      user_id: m.user_id || m.user?.id || m.id,
      username: m.username || m.user?.username || m.name || '',
    }))
    .filter((m) => {
      if (!m.user_id || inChannel.has(m.user_id) || m.user_id === currentUser?.id) return false
      if (seen.has(m.user_id)) return false
      seen.add(m.user_id)
      return !q || m.username.toLowerCase().includes(q)
    })

  const run = async (userId, fn) => {
    setBusy(userId)
    try { await fn() } catch { /* logged in the context; the roster simply stays */ }
    setBusy(null)
  }
  const add = (m) => run(m.user_id, async () => {
    await addChannelMember(channel.id, m.user_id, m.username)
    setQuery('')
  })
  const remove = (m) => run(m.user_id, () => removeChannelMember(channel.id, m.user_id))

  return (
    <span className={`prop-select channel-members${editable ? ' is-editable' : ''}`} ref={ref}>
      <span className="prop-chips">
        {members.map((m) => {
          const name = m.username || '?'
          const self = m.user_id === currentUser?.id
          return (
            <span key={m.user_id} className="prop-chip channel-member-chip" data-busy={busy === m.user_id || undefined}>
              <span className="header-member-avatar" style={{ '--av-color': avatarColor?.(name) }}>
                {name[0].toUpperCase()}
              </span>
              {name}
              {editable && !self && (
                <button
                  type="button"
                  className="prop-chip-x"
                  aria-label={`Remove ${name}`}
                  disabled={busy === m.user_id}
                  onClick={() => remove(m)}
                >
                  ×
                </button>
              )}
            </span>
          )
        })}
        {editable && (
          <button
            type="button"
            className="prop-chip channel-members-add"
            aria-label="Add member"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            +
          </button>
        )}
      </span>
      {open && editable && (
        <div className="prop-select-menu">
          <input
            className="prop-select-input"
            autoFocus
            placeholder="Add from the group…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && candidates[0]) { e.preventDefault(); add(candidates[0]) } }}
          />
          {candidates.map((m) => (
            <button
              type="button"
              key={m.user_id}
              className="prop-select-opt"
              disabled={busy === m.user_id}
              onClick={() => add(m)}
            >
              {m.username || m.user_id.slice(0, 8)}
            </button>
          ))}
          {candidates.length === 0 && (
            <span className="prop-empty channel-members-none">
              {q ? 'No one in the group by that name' : 'Everyone in the group is here'}
            </span>
          )}
        </div>
      )}
    </span>
  )
}
